<template>
    <div class="overview">
        <Tabs tabsMainClass="tabs-with-underline" :wrap="false" v-slot="tabsProps" :key="tabRenderKey">
            <Tab @tabMounted="tabsProps.onTabMounted"
                :activeTab="tabsProps.activeTab"
                :title="$t('CoursePage.about_this_course')"
            >
                <div class="overview__course-info">
                    <TotemCourseInfo
                        :categories="course.categoriesWithAncestors"
                        :level="course.level"
                        :certs="course.certifiedBy"
                        :isLive="isLive(course)"
                        :liveEventDate="getLiveEventDate(course)"
                        :liveEventTime="getLiveEventTime(course)"
                        :authorizationId="course.authorizationId"
                        :durations="course.durations"
                        :certifiedBy="course.certifiedBy"
                        :title="course.title"
                        :course="course"
                        theme="highlight"

                        :publishedDate="course.publishedDate" 
                        :expirationDate="course.expirationDate" 
                        :lastUpdatedDate="course.checkedAt"
                    />
                </div>
                <div class="overview__description" v-html="truncateDescription"></div>
                <FreeTrialBox v-if="$isLangDk && !isLoggedIn && isMobileView" />
            </Tab>
            <Tab 
                v-if="isMobileView"
                @tabMounted="tabsProps.onTabMounted"
                :activeTab="tabsProps.activeTab"
                :title="$t('CoursePage.contents')"
            >
                <slot></slot>
            </Tab>
            <Tab v-if="isLoggedIn" @tabMounted="tabsProps.onTabMounted"
                :activeTab="tabsProps.activeTab"
                :title="$t('CoursePage.notes')"
            >
                <div class="overview__notes">
                    <div class="overview__notes-content">
                        <CourseOverviewEditor :courseId="course.id" :courseTitle="course.title" v-if="isLoggedIn" />
                    </div>
                </div>                
            </Tab>
            <Tab @tabMounted="tabsProps.onTabMounted"
                :activeTab="tabsProps.activeTab"
                :title="$t('CoursePage.instructors')"
                v-if="course.instructor || (course.instructors && course.instructors.length > 0)"
            >
                <div v-if="(course.instructors && course.instructors.length > 0)" class="overview__instructors">
                    <div class="overview__instructor" v-for="(instructor, index) in course.instructors" :key="index">
                        <div class="overview__instructor-image" v-if="instructorImage(instructor)">
                            <img :src="instructorImage(instructor)" :alt="instructor.name" class="overview__instructor-image"/>
                        </div>
                        <div class="overview__instructor-info">
                            <div class="row">
                                <h2 class="overview__instructor-name" @click="goToListWithPreFilteredOption(instructorObject(instructor), 'instructors')">{{instructor.name}}</h2>
                            </div>
                            <div class="row overview__instructor-title" v-if="instructor.title">
                                {{instructor.title}}
                            </div>
                            <div class="row overview__instructor-organization" v-if="instructor.organization">
                                {{instructor.organization}}
                            </div>
                            <div class="row -v-html overview__instructor-description" v-if="instructor.description" v-html="instructor.description"></div>
                        </div>
                    </div>
                </div>
            </Tab>
            <Tab @tabMounted="tabsProps.onTabMounted"
                :activeTab="tabsProps.activeTab"
                :title="$t('CoursePage.materials')"
                v-if="isDesktopView && file && file.length > 0"
            >
                <object class="overview__materials" :data="file" :type="getExtentionOfFile" width="100%" height="600px">
                    <template v-if="getExtentionOfFile === 'application/file'">
                        <p> {{ $t('CourseOverview.if_no_pdf_extension') }}
                        <TotemLink
                            class="overview__button"
                            color="basic"
                            target="_blank"
                            rel="noopener noreferrer"
                            :href="file"
                            :label="$t('CourseOverview.click_to_download')"
                            icon="file-pdf">
                        </TotemLink>
                        </p>
                    </template>
                    <template v-else>
                        <div>
                        <TotemLink
                            class="overview__button"
                            color="basic"
                            target="_blank"
                            rel="noopener noreferrer"
                            :href="file"
                            :label="$t('CourseOverview.click_to_download')"
                            icon="file-pdf">
                        </TotemLink>
                        </div>
                    </template>
                </object>
            </Tab>
        </Tabs>
    </div>
</template>

<script>
    import CourseOverviewEditor from 'components/coursepage/courseoverview/CourseOverviewEditor'
    import goToListWithPreFilteredOption from 'components/shared/mixins/goToListWithPreFilteredOption'
    import CourseDates from 'components/coursepage/courseaddons/CourseDates'
    import FreeTrialBox from 'components/coursepage/freetrial/FreeTrialBox'
    import { mapGetters } from 'vuex'
    import Tab from 'components/layout/Tab'
    import Tabs from 'components/layout/Tabs'

    export default {
        components: {
            CourseOverviewEditor,
            CourseDates,
            Tab,
            Tabs,
            FreeTrialBox
        },
        mixins: [ goToListWithPreFilteredOption ],
        data () {
            return {
                desktopDescLength: 1500,
                mobileDescLength: 500,
                tabRenderKey: 0
            }
        },
        props: {
            course: Object,
            isLoggedIn: Boolean,
            file: String
        },
        computed: {
            ...mapGetters({
                isDigitalClassRoomTraining: 'Courses/isDigitalClassRoomTraining',
                currentMicroLearningSection: 'MicroLearning/getMicroLearningSection',
                isLive: 'Courses/isLive',
                getLiveEventDate: 'Courses/getLiveEventDate',
                getLiveEventTime: 'Courses/getLiveEventTime'
            }),
            getExtentionOfFile () {
                const parts = this.file.split('.')
                return `application/${parts[parts.length - 1]}`
            },
            isDigitalClassRoom () {
                return this.isDigitalClassRoomTraining(this.course)
            },
            isMicroLearningCourseVideoSection () {
                if (!this.currentMicroLearningSection) {
                    return true
                }
                return this.currentMicroLearningSection.type !== 'file'
            },
            areThereValidTabs () {
                return (this.file && this.file.length > 0) || (this.course.instructor && this.course.instructor.name !== '')
            },
            truncateDescription () {
                let length = this.isDesktopView ? this.desktopDescLength : this.mobileDescLength
                if (this.currentMicroLearningSection === null || this.currentMicroLearningSection.type === 'video') {
                    return this.truncate(this.course.description, length)
                } else {
                    return this.truncate(this.currentMicroLearningSection.description, length)
                }
            }
        },
        methods: {
            truncate (text, length) {
                if (text.length < length) {
                    return text
                } else {
                    return text.substring(0, length) + '...'
                }
            },
            openMaterial (url) {
                window.open(url, '_blank')
            },
            getLabelForMaterialButton (index) {
                return this.$t('CourseOverview.material') + ' ' + parseInt(index + 1)
            },
            instructorImage (instructor) {
                if (!instructor || !instructor.avatarUrl) {
                    return null
                }
                let image = instructor.avatarUrl.x1
                return image
            },
            instructorObject (instructor) {
                return {
                    id: instructor.instructor.id,
                    label: instructor.instructor.name
                }
            }
        },
        watch: {
            isMobileView: {
                handler () {
                    this.tabRenderKey++
                },
                deep: true
            }
        }
    }
</script>

<style lang="scss">
    @include tabs_with_underline;

    .overview {
        margin: 0 0 20px;
        font-family: 'PlusJakartaSans-Regular', sans-serif;

        [class*="mobile"] & {
            margin: 20px 0 20px;
        }

        [class*="desktop_xxxl"] & {
            width: 1055px;
        }

        .row {
            margin-bottom: 15px;
        }

        &__button {
          align-self: center;
        }

        &__instructors {
            flex-direction: column;
            display: flex;
            gap: 30px;
        }

        &__instructor {
            display: flex;
            flex-direction: row;

            [class*="mobile_sm"] & {
                flex-direction: column;
            }
        }

        &__instructor-image {
            margin: 0 0 10px;

            [class*="mobile_md"] &,
            [class*="desktop"] & {
                float: left;
                margin: 0 40px 0 0;
            }
            [class*="mobile_sm"] & {
                margin: 0 0 20px;
            }
        }

        &__instructor-image {
            object-fit: cover;
            height: 198px;
            width: 198px;
            border-radius: 50%;
        }

        &__instructor-info {
            display: flex;
            justify-content: center;
            flex-direction: column;
        }

        &__instructor-name {
            font-size: 2.4rem;
            font-family: 'PlusJakartaSans-Bold', sans-serif;
        }

        &__instructor-description {
            font-size: 1.6rem;
            line-height: 150%;
        }

        &__instructor-title {
            font-size: 1.8rem;
            color: $lavender;
        }

        &__instructor-organization {
            font-size: 1.4rem;
        }

        &__materials-list-item {
            padding: 5px;
        }

        &__course-info {
            background: $concrete;
            border-radius: 4px;
            padding: 10px;
            margin: 30px 0;
            display: flex;
            flex-wrap: wrap;
        }
        &__description {
            line-height: 150%;
        }

        &__notes, &__instructors, &__materials {
            margin: 30px 0 0 0;
        }

        &__notes {
            display: flex;
            justify-content: center;
            width: 100%;
        }

        &__notes-content {
            width: 100%;
            max-width: 1055px;
            box-sizing: border-box;
        }
    }
</style>
